import React, { useState, useEffect } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Divider,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import SendIcon from "@mui/icons-material/Send";
import InboxIcon from "@mui/icons-material/Inbox";
import DraftsIcon from "@mui/icons-material/Drafts";
import PageTitle from "../../../components/Common/PageTitle";
import { CustomBtn } from "../../../components/Common/CustomBtn";
import {
  ConfirmationSnackbar,
  ErrorSnackbar,
} from "../../../components/Common/CustomSnackbar";
import ComposeMessageModal from "../../../components/Modals/ComposeMessageModal";
import { useMessages } from "./Messages.hooks";
import { useSelector } from "react-redux";
import { colors, fonts } from "../../../theme/theme";

const MessagesPage = () => {
  const { userData } = useSelector((state) => state.user);
  const {
    contacts,
    messages,
    loading,
    sending,
    error,
    success,
    setError,
    setSuccess,
    sendMessage,
    markAsRead,
  } = useMessages();

  const [composeOpen, setComposeOpen] = useState(false);
  const [preSelectedContact, setPreSelectedContact] = useState(null);
  const [activeFilter, setActiveFilter] = useState("all"); // "all" | "received" | "sent"
  const [expandedId, setExpandedId] = useState(null); // id of the currently expanded message (only one at a time)

  const toggleExpanded = (msgId, isUnread) => {
    setExpandedId((prev) => (prev === msgId ? null : msgId));
    if (isUnread) markAsRead(msgId);
  };

  // Strips HTML while preserving paragraph/line breaks as actual newlines
  // so that the expanded body renders with sensible spacing via pre-wrap.
  const htmlToPlainText = (html) => {
    if (!html) return "";
    return html
      .replace(/<\s*br\s*\/?\s*>/gi, "\n")
      .replace(/<\/\s*(p|div|li|h[1-6])\s*>/gi, "\n")
      .replace(/<[^>]*>/g, "")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  };

  // Reset expansion when the filter changes so stale expanded ids do not
  // silently re-appear when the user switches filters back.
  useEffect(() => {
    setExpandedId(null);
  }, [activeFilter]);

  const handleOpenCompose = (contact = null) => {
    setPreSelectedContact(contact);
    setComposeOpen(true);
  };

  const handleCloseCompose = () => {
    setComposeOpen(false);
    setPreSelectedContact(null);
  };

  const handleSend = async (data) => {
    try {
      await sendMessage(data);
      handleCloseCompose();
    } catch {
      // Error is already set in the hook via setError
    }
  };

  const filteredMessages = messages.filter((msg) => {
    if (activeFilter === "received") return msg.toUserId === userData?.id;
    if (activeFilter === "sent") return msg.fromUserId === userData?.id;
    return true;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    if (isToday) {
      return date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    }
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year:
        date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    });
  };

  const getContactName = (user) => {
    if (!user) return "Unknown";
    return (
      `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email
    );
  };

  if (loading) {
    return (
      <Box>
        <PageTitle noIcon text="Send and receive messages" title="Messages" />
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 300,
          }}
        >
          <CircularProgress size={50} sx={{ color: colors.primary }} />
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <PageTitle noIcon text="Send and receive messages" title="Messages" />

      <ConfirmationSnackbar
        snackbarState={success}
        handleSnackbarClose={() => setSuccess(false)}
        text="Message sent successfully!"
      />
      <ErrorSnackbar
        error={!!error}
        handleCloseError={() => setError(null)}
        errorMsg={error}
      />

      {/* Action bar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {["all", "received", "sent"].map((filter) => (
            <Chip
              key={filter}
              label={filter.charAt(0).toUpperCase() + filter.slice(1)}
              onClick={() => setActiveFilter(filter)}
              icon={
                filter === "all" ? (
                  <EmailIcon />
                ) : filter === "received" ? (
                  <InboxIcon />
                ) : (
                  <SendIcon />
                )
              }
              sx={{
                fontFamily: fonts.gotham,
                fontWeight: activeFilter === filter ? 700 : 400,
                fontSize: "13px",
                backgroundColor:
                  activeFilter === filter
                    ? `${colors.secondary}15`
                    : "transparent",
                border: `1px solid ${activeFilter === filter ? colors.secondary : colors.lightBorder}`,
                color:
                  activeFilter === filter
                    ? colors.secondary
                    : colors.text,
                "&:hover": {
                  backgroundColor: `${colors.secondary}10`,
                },
              }}
            />
          ))}
        </Box>

        <CustomBtn
          text="Compose"
          noIcon
          onClick={() => handleOpenCompose()}
          size="sm"
        />
      </Box>

      {/* Contacts quick bar (for practitioners: their clients, for clients: their practitioners) */}
      {contacts.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontFamily: fonts.gotham,
              fontWeight: 600,
              fontSize: "13px",
              color: colors.textLight,
              mb: 1,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            {userData?.role === "patient"
              ? "Your Practitioners"
              : "Your Clients"}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {contacts.map((contact) => (
              <Chip
                key={contact.id}
                label={getContactName(contact)}
                onClick={() => handleOpenCompose(contact)}
                icon={<EmailIcon sx={{ fontSize: "16px !important" }} />}
                sx={{
                  fontFamily: fonts.gotham,
                  fontSize: "13px",
                  border: `1px solid ${colors.lightBorder}`,
                  backgroundColor: colors.white,
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: `${colors.secondary}08`,
                    borderColor: colors.secondary,
                  },
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      <Divider sx={{ mb: 3 }} />

      {/* Messages list */}
      {filteredMessages.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: 8,
            px: 3,
          }}
        >
          <EmailIcon
            sx={{ fontSize: 64, color: colors.lightBorder, mb: 2 }}
          />
          <Typography
            sx={{
              fontFamily: fonts.gotham,
              fontWeight: 600,
              fontSize: "18px",
              color: colors.primary,
              mb: 1,
            }}
          >
            {activeFilter === "all"
              ? "No messages yet"
              : `No ${activeFilter} messages`}
          </Typography>
          <Typography
            sx={{
              fontFamily: fonts.gotham,
              fontSize: "14px",
              color: colors.textLight,
              mb: 3,
            }}
          >
            {contacts.length > 0
              ? "Start a conversation by clicking on a contact above or using the Compose button."
              : "Contacts will appear here once you have appointments or payment history."}
          </Typography>
          {contacts.length > 0 && (
            <CustomBtn
              text="Send First Message"
              noIcon
              onClick={() => handleOpenCompose()}
            />
          )}
        </Box>
      ) : (
        <Box
          sx={{
            border: `1px solid ${colors.lightBorder}`,
            borderRadius: "14px",
            overflow: "hidden",
            backgroundColor: colors.white,
          }}
        >
          {filteredMessages.map((msg, index) => {
            const isSent = msg.fromUserId === userData?.id;
            const otherUser = isSent ? msg.recipient : msg.sender;
            const isUnread = !isSent && !msg.readAt;
            const isExpanded = expandedId === msg.id;
            const plainBody = htmlToPlainText(msg.body);
            const isTruncatable = plainBody.length > 120;

            return (
              <Box
                key={msg.id}
                role="button"
                tabIndex={0}
                aria-expanded={isExpanded}
                onClick={() => toggleExpanded(msg.id, isUnread)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleExpanded(msg.id, isUnread);
                  }
                }}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 2,
                  p: 2,
                  cursor: "pointer",
                  backgroundColor: isUnread
                    ? `${colors.secondary}06`
                    : "transparent",
                  borderBottom:
                    index < filteredMessages.length - 1
                      ? `1px solid ${colors.lightBorder}40`
                      : "none",
                  transition: "background-color 0.15s ease",
                  "&:hover": {
                    backgroundColor: `${colors.secondary}04`,
                  },
                  "&:focus-visible": {
                    outline: `2px solid ${colors.secondary}`,
                    outlineOffset: "-2px",
                  },
                }}
              >
                {/* Direction indicator */}
                <Box sx={{ pt: 0.5, minWidth: 24 }}>
                  {isSent ? (
                    <Tooltip title="Sent">
                      <SendIcon
                        sx={{ fontSize: 18, color: colors.textLight }}
                      />
                    </Tooltip>
                  ) : isUnread ? (
                    <Tooltip title="Unread">
                      <EmailIcon
                        sx={{ fontSize: 18, color: colors.secondary }}
                      />
                    </Tooltip>
                  ) : (
                    <Tooltip title="Read">
                      <DraftsIcon
                        sx={{ fontSize: 18, color: colors.textLight }}
                      />
                    </Tooltip>
                  )}
                </Box>

                {/* Message content */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: fonts.gotham,
                        fontWeight: isUnread ? 700 : 500,
                        fontSize: "14px",
                        color: colors.primary,
                      }}
                    >
                      {isSent ? `To: ${getContactName(otherUser)}` : getContactName(otherUser)}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: fonts.gotham,
                        fontSize: "12px",
                        color: colors.textLight,
                        whiteSpace: "nowrap",
                        ml: 1,
                      }}
                    >
                      {formatDate(msg.sentAt)}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: fonts.gotham,
                      fontWeight: isUnread ? 600 : 400,
                      fontSize: "13px",
                      color: colors.primary,
                      mb: 0.3,
                    }}
                  >
                    {msg.subject}
                  </Typography>
                  {isExpanded ? (
                    <Typography
                      sx={{
                        fontFamily: fonts.gotham,
                        fontSize: "13px",
                        color: colors.textLight,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        mb: isTruncatable ? 1 : 0,
                      }}
                    >
                      {plainBody}
                    </Typography>
                  ) : (
                    <Typography
                      noWrap
                      sx={{
                        fontFamily: fonts.gotham,
                        fontSize: "12px",
                        color: colors.textLight,
                      }}
                    >
                      {plainBody.replace(/\s+/g, " ").substring(0, 120)}
                      {isTruncatable ? "..." : ""}
                    </Typography>
                  )}
                  {isTruncatable && (
                    <Typography
                      component="span"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleExpanded(msg.id, isUnread);
                      }}
                      sx={{
                        display: "inline-block",
                        mt: 0.5,
                        fontFamily: fonts.gotham,
                        fontSize: "12px",
                        fontWeight: 600,
                        color: colors.secondary,
                        cursor: "pointer",
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      }}
                    >
                      {isExpanded ? "Show less" : "Show more"}
                    </Typography>
                  )}
                </Box>

                {/* Quick reply */}
                {!isSent && otherUser && (
                  <Box sx={{ pt: 0.5 }}>
                    <Tooltip title={`Reply to ${getContactName(otherUser)}`}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenCompose(otherUser);
                        }}
                        sx={{
                          color: colors.secondary,
                          "&:hover": {
                            backgroundColor: `${colors.secondary}10`,
                          },
                        }}
                      >
                        <SendIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {/* Compose modal */}
      <ComposeMessageModal
        open={composeOpen}
        handleClose={handleCloseCompose}
        contacts={contacts}
        preSelectedContact={preSelectedContact}
        onSend={handleSend}
        sending={sending}
      />
    </Box>
  );
};

export default MessagesPage;
